import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from '../../styled';

/**
 * Lightweight motion primitives for the marketing site.
 *
 * We intentionally avoid framer-motion & co. because the site runs
 * React 16 + Preact in production. Everything here is IntersectionObserver +
 * CSS transitions, honors prefers-reduced-motion, and is SSR-safe.
 */

export const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(query.matches);
    const listener = (e: MediaQueryListEvent) => setReduced(e.matches);
    // Older Safari only supports addListener
    if (query.addEventListener) {
      query.addEventListener('change', listener);
      return () => query.removeEventListener('change', listener);
    }
    query.addListener(listener);
    return () => query.removeListener(listener);
  }, []);

  return reduced;
};

export const useInView = <T extends Element>({
  threshold = 0.25,
  rootMargin = '0px 0px -40px 0px',
  once = true
}: {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
} = {}) => {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === 'undefined') {
      return;
    }
    if (!('IntersectionObserver' in window)) {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            if (once) {
              observer.disconnect();
            }
          } else if (!once) {
            setInView(false);
          }
        });
      },
      { threshold, rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return { ref, inView };
};

const RevealWrapper = styled('div')<{
  visible: boolean;
  delayMs: number;
  distancePx: number;
  reduced: boolean;
}>`
  opacity: ${(p) => (p.visible || p.reduced ? 1 : 0)};
  transform: ${(p) =>
    p.visible || p.reduced ? 'none' : `translateY(${p.distancePx}px)`};
  transition: opacity 0.7s ease ${(p) => p.delayMs}ms,
    transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${(p) => p.delayMs}ms;
  will-change: opacity, transform;
`;

/**
 * Scroll-in reveal. Children fade and slide up when they enter the viewport.
 * Use `delay` (ms) to stagger siblings.
 */
export const Reveal = ({
  children,
  delay = 0,
  distance = 24,
  className,
  style
}: {
  children: React.ReactNode;
  delay?: number;
  distance?: number;
  className?: string;
  style?: React.CSSProperties;
}) => {
  const reduced = usePrefersReducedMotion();
  const { ref, inView } = useInView<HTMLDivElement>();

  return (
    <RevealWrapper
      ref={ref}
      visible={inView}
      delayMs={delay}
      distancePx={distance}
      reduced={reduced}
      className={className}
      style={style}
    >
      {children}
    </RevealWrapper>
  );
};

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

/**
 * Number that counts up from 0 when it first scrolls into view.
 * Pass `format` to control rendering, e.g. (n) => `$${n.toLocaleString()}`.
 */
export const AnimatedNumber = ({
  value,
  duration = 1400,
  format,
  start
}: {
  value: number;
  duration?: number;
  format?: (n: number) => string;
  /** External gate; when omitted, starts when scrolled into view. */
  start?: boolean;
}) => {
  const reduced = usePrefersReducedMotion();
  const { ref, inView } = useInView<HTMLSpanElement>({ threshold: 0.4 });
  const [current, setCurrent] = useState(0);
  const frameRef = useRef<number>();
  const started = start !== undefined ? start : inView;

  useEffect(() => {
    if (!started) {
      return;
    }
    if (reduced) {
      setCurrent(value);
      return;
    }
    const startTime = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      setCurrent(value * easeOutCubic(progress));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      }
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [started, value, duration, reduced]);

  const formatter = useMemo(
    () => format || ((n: number) => Math.round(n).toLocaleString('en-US')),
    [format]
  );

  return <span ref={ref}>{formatter(current)}</span>;
};
